import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ButtonLoading from "../Button loading/ButtonLoading";
import { MdCancel } from "react-icons/md";
import { FiUpload } from "react-icons/fi";
import {
  useCreateManagementProfileMutation,
  useGetProfileByIdQuery,
  useUpdateManagementProfileMutation,
} from "../../redux/managementProfileApi";
import { toast } from "react-toastify";
import { showErrorToast } from "../../utils/Utils";

const AddManagementProfile = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { data, isLoading, isFetching } = useGetProfileByIdQuery(id, {
    refetchOnFocus: false,
    skip: !isEditMode,
  });
  const [createManagementProfile, { isLoading: createLoading }] =
    useCreateManagementProfileMutation();
  const [updateManagementProfile, { isLoading: updateLoading }] =
    useUpdateManagementProfileMutation();
  const [profileImage, setProfileImage] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    position: "",
    email: "",
    postCategory: "",
  });
  const postCategories = [
    "Chairman",
    "Director General",
    "Additional Director General",
    "Chief Planner",
    "Chief Engineer",
    "Director",
    "Deputy Director",
    "Assistant Director",
  ];

  useEffect(() => {
    if (isEditMode && data?.profile) {
      setFormData({
        fullName: data?.profile?.fullName || "",
        position: data?.profile?.position || "",
        email: data?.profile?.email || "",
        postCategory: data?.profile?.postCategory || "",
      });

      if (data?.profile?.profileImage?.imageUrl) {
        setProfileImage({
          src: data?.profile?.profileImage?.imageUrl,
          file: null,
        });
      }
    }
  }, [isEditMode, data]);

  const [errors, setErrors] = useState({
    fullName: "",
    position: "",
    email: "",
    postCategory: "",
  });
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };
  const fileInputRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    if (!file) {
      setError("No file selected.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      if (img.width < 220 || img.height < 250) {
        setError(
          "Image size too small. Minimum required dimensions are 220×250 pixels.",
        );
        URL.revokeObjectURL(objectUrl);
        return;
      }

      if (img.width > 1000 || img.height > 1000) {
        setError("Image too large. Maximum allowed size is 1000×1000 pixels.");
        URL.revokeObjectURL(objectUrl);
        return;
      }

      const newImage = {
        src: objectUrl,
        file,
      };

      setProfileImage(newImage);
    };

    img.onerror = () => {
      setError("Invalid image file.");
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDelete = () => {
    setProfileImage(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "FullName is required.";
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address.";
      isValid = false;
    }
    if (!formData?.position) {
      newErrors.position = "Position/Title is required.";
      isValid = false;
    }
    if (!formData?.postCategory) {
      newErrors.postCategory = "Management Post Category is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCancel = () => {
    setFormData({
      fullName: "",
      position: "",
      email: "",
      postCategory: "",
    });
    setProfileImage(null);
    setErrors({
      fullName: "",
      position: "",
      email: "",
      postCategory: "",
    });
    setError("");
  };
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    const userData = new FormData();
    userData.append("fullName", formData.fullName);
    userData.append("position", formData.position);
    userData.append("email", formData.email);
    userData.append("postCategory", formData.postCategory);
    if (profileImage?.file) {
      userData.append("profileImage", profileImage?.file);
    }

    try {
      let response = null;
      if (isEditMode) {
        response = await updateManagementProfile({
          id,
          profileData: userData,
        }).unwrap();
      } else {
        response = await createManagementProfile(userData).unwrap();
      }

      if (response.success) {
        handleCancel();
        toast.success(response.message);
        navigate("/management");
      }
    } catch (err) {
      showErrorToast(err);
    }
  };

  return (
    <div className="flex flex-col gap-3 bg-white rounded-xl xl:p-8 lg:p-6 p-4 ">
      <div className="flex items-center justify-between">
        <h1 className="text-[#05004E] lg:text-[1.8rem] md:text-[1.4rem] text-[1.2rem] font-semibold">
          {isEditMode ? "Update" : "Add New"} Profile
        </h1>
        <div className="flex items-center justify-end lg:gap-4 md:gap-3 gap-2">
          <p
            onClick={() => handleCancel()}
            className="xl:w-[10rem] lg:w-[9rem] md:w-[8rem] w-max md:py-2 md:px-0 py-1 px-2 lg:text-[1rem] md:text-sm text-[13px]  border  border-[#00643B] text-[#00643B] text-center cursor-pointer  lg:rounded-xl md:rounded-lg rounded-md font-medium"
          >
            Cancel
          </p>

          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={createLoading || updateLoading ? true : false}
            className="xl:w-[10rem] lg:w-[9rem] md:w-[8rem] w-max md:py-2 md:px-0 py-1 px-2 lg:text-[1rem] md:text-sm text-[13px]  border border-[#00643B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white bg-[#00643B] lg:rounded-xl md:rounded-lg rounded-md font-medium"
          >
            {createLoading || updateLoading ? (
              <ButtonLoading text="Saving..." />
            ) : (
              "Save Profile"
            )}
          </button>
        </div>
      </div>
      {isFetching || isLoading ? (
        <AddProfileSkeleton />
      ) : (
        <form className="flex items-start md:flex-row flex-col xl:gap-6 lg:gap-4 md:gap-3 gap-2 lg:text-[1rem] text-[.9rem]">
          <div className="xl:w-[25%] md:w-[35%] w-full flex flex-col gap-4 ">
            <div className=" w-full flex flex-col gap-1">
              <label className="font-semibold text-[#151D48]">
                Profile Photo *
              </label>
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className=" w-full bg-[#F3F4F6] border-2 border-[#D1D5DC] rounded-2xl aspect-[1/1.2] overflow-hidden"
              >
                {!profileImage ? (
                  <div className=" w-full h-full flex flex-col gap-1 items-center justify-center text-[#99A1AF]">
                    <FiUpload className=" size-8" />
                    <p>No image uploaded</p>
                  </div>
                ) : (
                  <div className=" relative flex justify-center items-center w-full h-full p-4">
                    <img
                      src={profileImage.src}
                      alt="Upload Preview"
                      className=" object-cover w-full h-full md:rounded-[1rem] rounded-lg max-w-full max-h-full"
                    />
                    {!isEditMode && (
                      <button
                        onClick={() => handleDelete()}
                        className="absolute top-2 right-2 bg-white rounded-full  "
                      >
                        <MdCancel className="lg:size-8 size-6 text-red-500" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <label
              htmlFor="file-upload"
              className=" w-full bg-[#00643B] rounded-2xl text-white lg:p-4 md:p-3 p-2 cursor-pointer flex items-center justify-center gap-2"
            >
              <FiUpload className=" size-4" />
              Upload Photo
              <input
                id="file-upload"
                type="file"
                accept="image/*"
                onChange={(e) => handleFile(e.target.files[0])}
                className="hidden"
                ref={fileInputRef}
              />
            </label>
            <div className="w-full bg-[#F9FAFB] text-[#737791] p-5 rounded-2xl flex flex-col">
              <p className=" xl:text-base text-sm  font-medium">
                Image Guidelines:
              </p>
              <p className=" xl:text-sm text-xs">Recommended: 400x500px</p>
              <p className=" xl:text-sm text-xs">Max file size: 5MB</p>
              <p className=" xl:text-sm text-xs">Formats: JPG, PNG, WebP</p>
              <p className=" xl:text-sm text-xs">Use professional headshot</p>
              {error && (
                <>
                  <p className=" xl:text-base text-sm text-red-500  font-medium">
                    Image Guidelines:
                  </p>
                  <p className=" xl:text-sm text-xs text-red-500">{error}</p>
                </>
              )}
            </div>
          </div>
          <div className="xl:w-[75%] md:w-[65%] w-full flex flex-col gap-4">
            <div className="w-full flex flex-col lg:gap-4 md:gap-3 gap-2 bg-[#F9FAFB] rounded-2xl p-5 overflow-hidden">
              <div>
                <h1 className=" text-[#00643B] xl:text-[1.5rem] font-semibold">
                  Personal Information
                </h1>
                <div className=" w-full h-[2px] bg-[#00643B]" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-[#151D48]">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData?.fullName}
                  onChange={handleChange}
                  placeholder="e.g., Dr. Sarah Ahmed"
                  className="w-full border border-[#D1D5DC] bg-transparent text-[#737791] placeholder:text-[#0A0A0A80] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
                />
                {errors.fullName && (
                  <p className="text-red-500 md:text-sm text-[12px] ">
                    {errors.fullName}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-[#151D48]">
                  Position/Title *
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData?.position}
                  onChange={handleChange}
                  placeholder="e.g., Director General"
                  className="w-full border border-[#D1D5DC] bg-transparent text-[#737791] placeholder:text-[#0A0A0A80] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
                />
                {errors.position && (
                  <p className="text-red-500 md:text-sm text-[12px] ">
                    {errors.position}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-[#151D48]">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                  placeholder="e.g., johndoe12@gmail.com"
                  className="w-full border border-[#D1D5DC] bg-transparent text-[#737791] placeholder:text-[#0A0A0A80] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
                />
                {errors.email && (
                  <p className="text-red-500 md:text-sm text-[12px] ">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>
            <div className="w-full flex flex-col lg:gap-4 md:gap-3 gap-2 bg-[#F9FAFB] rounded-2xl p-5 overflow-hidden">
              <div>
                <h1 className=" text-[#00643B] xl:text-[1.5rem] font-semibold">
                  Management Details
                </h1>
                <div className=" w-full h-[2px] bg-[#00643B]" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-[#151D48]">
                  Management Post Category *
                </label>
                <select
                  name="postCategory"
                  value={formData?.postCategory}
                  onChange={handleChange}
                  className="w-full border border-[#D1D5DC]  text-[#737791] placeholder:text-[#0A0A0A80] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
                >
                  {" "}
                  <option value="">Select Post Category</option>
                  {postCategories?.map((category, i) => (
                    <option key={i} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                {errors.postCategory && (
                  <p className="text-red-500 md:text-sm text-[12px] ">
                    {errors.postCategory}
                  </p>
                )}
              </div>
              <p className=" text-sm text-[#737791]">
                Select the appropriate management level for this profile
              </p>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

const AddProfileSkeleton = () => {
  return (
    <div className="p-6 animate-pulse">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left: Photo Upload Skeleton */}
        <div className="flex flex-col w-full lg:w-1/3">
          <div className="bg-gray-200 rounded-xl h-[300px] w-full"></div>
          <div className="mt-3 h-10 bg-gray-200 rounded-lg w-full"></div>
          <div className="mt-2 space-y-1">
            <div className="h-2 w-2/3 bg-gray-200 rounded"></div>
            <div className="h-2 w-1/2 bg-gray-200 rounded"></div>
            <div className="h-2 w-3/4 bg-gray-200 rounded"></div>
          </div>
        </div>

        {/* Right: Form Skeleton */}
        <div className="flex-1 flex flex-col gap-6">
          {/* Section Title */}
          <div className="h-5 w-1/3 bg-gray-200 rounded"></div>

          {/* Personal Info Fields */}
          <div className="bg-gray-100 rounded-xl p-5 grid grid-cols-1 gap-5">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex flex-col gap-2">
                <div className="h-3 w-1/2 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded-lg"></div>
              </div>
            ))}
          </div>

          {/* Management Details */}
          <div className="bg-gray-100 rounded-xl p-5 flex flex-col gap-3">
            <div className="h-3 w-1/2 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded-lg"></div>
          </div>

          {/* Buttons */}
        </div>
      </div>
    </div>
  );
};

export default AddManagementProfile;
